import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { SESSION_STORAGE_KEYS } from 'app/core/session_storage/constants/session-storage-keys.constant';
import { resolveAccountForTenant } from 'app/core/auth/account-selection.util';

export type PortalRole = 'user' | 'admin' | 'instanceadmin';

@Injectable({
  providedIn: 'root',
})
export class RoleContextService {
  private readonly activeRoleSubject = new BehaviorSubject<PortalRole | null>(null);

  public readonly activeRole$: Observable<PortalRole | null> = this.activeRoleSubject.asObservable();

  constructor(private msalService: MsalService) {
    this.refreshActiveRoleFromStorage();
  }

  public getAvailableRoles(): PortalRole[] {
    const account = this.getAccountForRoleRead();
    const roles = (account?.idTokenClaims?.roles as string[] | undefined) ?? [];

    const normalized = roles
      .map((r) => r.toLowerCase().trim())
      .filter((r) => r === 'user' || r === 'admin' || r === 'instanceadmin') as PortalRole[];

    // De-duplicate while preserving order
    return Array.from(new Set(normalized));
  }

  public getActiveRole(): PortalRole | null {
    return this.activeRoleSubject.getValue();
  }

  public setActiveRole(role: PortalRole): void {
    const availableRoles = this.getAvailableRoles();
    if (!availableRoles.includes(role)) {
      return;
    }

    this.activeRoleSubject.next(role);
    this.persistActiveRole(role);
  }

  public chooseDefaultRole(availableRoles = this.getAvailableRoles()): PortalRole | null {
    if (availableRoles.length === 0) {
      return null;
    }

    // Prefer saved role for current tenant.
    const saved = this.readActiveRoleFromStorage();
    if (saved && availableRoles.includes(saved)) {
      return saved;
    }

    // Fallback priority: admin > user > instanceadmin
    // (Instance admin is a global page that is used less frequently.)
    if (availableRoles.includes('admin')) return 'admin';
    if (availableRoles.includes('user')) return 'user';
    if (availableRoles.includes('instanceadmin')) return 'instanceadmin';

    return availableRoles[0];
  }

  public refreshActiveRoleFromStorage(): void {
    const availableRoles = this.getAvailableRoles();
    const nextRole = this.chooseDefaultRole(availableRoles);
    this.activeRoleSubject.next(nextRole);

    // If a role is now resolved (eg after login), persist it.
    if (nextRole) {
      this.persistActiveRole(nextRole);
    }
  }

  public clearActiveRoleForCurrentTenant(): void {
    const key = this.getStorageKeyForCurrentTenant();
    if (!key) {
      return;
    }
    localStorage.removeItem(key);
    this.activeRoleSubject.next(null);
  }

  private getAccountForRoleRead(): any {
    const accounts = this.msalService.instance.getAllAccounts();
    const storedTenantId = localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID);
    const activeAccount = this.msalService.instance.getActiveAccount();

    return resolveAccountForTenant(accounts, activeAccount, storedTenantId);
  }

  private getStorageKeyForCurrentTenant(): string | null {
    const tenantId = localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID);
    if (!tenantId) {
      return null;
    }

    return `ActiveRole:${tenantId}`;
  }

  private readActiveRoleFromStorage(): PortalRole | null {
    const key = this.getStorageKeyForCurrentTenant();
    if (!key) {
      return null;
    }

    const raw = localStorage.getItem(key);
    if (!raw) {
      return null;
    }

    const value = raw.toLowerCase().trim();
    if (value === 'user' || value === 'admin' || value === 'instanceadmin') {
      return value;
    }

    return null;
  }

  private persistActiveRole(role: PortalRole): void {
    const key = this.getStorageKeyForCurrentTenant();
    if (!key) {
      return;
    }

    localStorage.setItem(key, role);
  }
}
